import { Calendar } from '@liza/ui/components/ui/calendar';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@liza/ui/components/ui/input-group';
import { Popover, PopoverAnchor, PopoverContent } from '@liza/ui/components/ui/popover';
import { useDateLocale } from '@liza/ui/hooks/useDateLocale';
import { cn } from '@liza/ui/utils/cn';
import { DATE_FORMATS } from '@liza/ui/utils/date';
import { parseDate } from 'chrono-node';
import { format, isValid, parse } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface DatePickerProps {
  /** ISO `yyyy-MM-dd` value, or `''` when empty. */
  value: string;
  /** Called with the new ISO `yyyy-MM-dd` value, or `''` when cleared. */
  onChange: (value: string) => void;
  id?: string;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /** Open the calendar on focus when the field is empty (an empty field prompts the
   * picker; a filled one doesn't). Defaults to true. */
  openOnFocus?: boolean;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
  /** Accessible label for the calendar toggle button. */
  calendarButtonLabel?: string;
  className?: string;
  'data-testid'?: string;
}

/** Parse a `yyyy-MM-dd` value into a Date at local midnight, or undefined when empty/invalid. */
function parseValue(value: string): Date | undefined {
  if (!value) return undefined;
  const date = parse(value, DATE_FORMATS.ISO_DATE, new Date());
  return isValid(date) ? date : undefined;
}

// Bounds for the calendar's month/year dropdowns. Computed once at module load
// (the 10-year forward buffer makes the exact load-time year immaterial).
const CALENDAR_START_MONTH = new Date(1900, 0);
const CALENDAR_END_MONTH = new Date(new Date().getFullYear() + 10, 11);

/**
 * Natural-language date picker (shadcn "Natural Language Picker"). The field accepts
 * free text parsed by chrono-node — "tomorrow", "next monday", "june 2", "in 3 days",
 * "6/3/26" — and the calendar (which mirrors what's typed) opens via the calendar
 * button, ArrowDown, or automatically when an empty field gains focus. A muted hint
 * shows the resolved date only when the text isn't already in the canonical display
 * form. Replaces the native `<input type="date">`, whose indicator icon is invisible
 * in dark mode. Value is the ISO `yyyy-MM-dd` string used by FHIR form state.
 */
export function DatePicker({
  value,
  onChange,
  id,
  onBlur,
  placeholder,
  required,
  disabled,
  openOnFocus = true,
  calendarButtonLabel = 'Open calendar',
  className,
  ...props
}: DatePickerProps): React.ReactNode {
  const { locale } = useDateLocale();
  const selected = parseValue(value);
  const formatDisplay = (date: Date | undefined | null): string =>
    date ? format(date, DATE_FORMATS.DATE, { locale }) : '';

  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => formatDisplay(selected));
  const [month, setMonth] = useState<Date | undefined>(selected);
  const fieldRef = useRef<HTMLDivElement>(null);

  // Mirror the committed value into the field when it changes externally (form reset)
  // or when the field loses focus — but leave the raw text alone while typing. Reset
  // the calendar's viewed month too, so a cleared field reopens on today, not the
  // last-viewed month.
  useEffect(() => {
    if (!focused) {
      const parsed = parseValue(value);
      setText(formatDisplay(parsed));
      setMonth(parsed);
    }
    // formatDisplay closes over `locale`; `value` and `focused` are the real inputs.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [value, focused, locale]);

  const liveParsed = useMemo(() => (text.trim() ? parseDate(text) : null), [text]);

  const commit = (date: Date): void => {
    onChange(format(date, DATE_FORMATS.ISO_DATE));
    setMonth(date);
  };

  const handleTextChange = (next: string): void => {
    setText(next);
    if (next.trim() === '') {
      onChange('');
      return;
    }
    const parsed = parseDate(next);
    if (parsed) commit(parsed);
  };

  // Only surface the resolved date when the text isn't already the canonical display
  // (so typing/selecting "Jun 3, 2026" doesn't echo "Jun 3, 2026" back underneath).
  const showHint = focused && !!liveParsed && text.trim() !== formatDisplay(liveParsed);

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <InputGroup ref={fieldRef}>
            <InputGroupInput
              id={id}
              value={text}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
              onChange={(e) => handleTextChange(e.target.value)}
              onFocus={() => {
                setFocused(true);
                // Prompt the picker on an empty field, but don't reopen one that's already set.
                if (openOnFocus && !disabled && !selected) setOpen(true);
              }}
              onBlur={() => {
                setFocused(false);
                onBlur?.();
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setOpen(true);
                } else if (e.key === 'Escape') {
                  setOpen(false);
                }
              }}
              {...props}
            />
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                size="icon-xs"
                disabled={disabled}
                aria-label={calendarButtonLabel}
                onClick={() => setOpen((prev) => !prev)}
              >
                <CalendarIcon />
              </InputGroupButton>
            </InputGroupAddon>
          </InputGroup>
        </PopoverAnchor>
        <PopoverContent
          className="w-auto p-0"
          align="end"
          // Keep the caret in the input so the user can type while the calendar is open.
          onOpenAutoFocus={(e) => e.preventDefault()}
          // Don't snap focus back to the field on close — that would retrigger open-on-focus.
          onCloseAutoFocus={(e) => e.preventDefault()}
          // The field lives outside the portaled content, so without this guard Radix would
          // treat typing/clicking in it as "outside" and dismiss while the user is still editing.
          onInteractOutside={(e) => {
            const target = e.detail.originalEvent.target as Node | null;
            if (target && fieldRef.current?.contains(target)) e.preventDefault();
          }}
          // Clicking a day must not blur the still-focused input: the resulting re-render
          // (focus state + text resync) lands between pointerdown and mouseup and eats the
          // first click. Preventing the pointerdown's default keeps focus in the input while
          // the day's click still fires, so the first selection registers.
          onPointerDown={(e) => {
            if ((e.target as HTMLElement).closest('[data-day]')) e.preventDefault();
          }}
        >
          <Calendar
            mode="single"
            selected={selected}
            month={month}
            onMonthChange={setMonth}
            captionLayout="dropdown"
            startMonth={CALENDAR_START_MONTH}
            endMonth={CALENDAR_END_MONTH}
            onSelect={(date) => {
              if (!date) return;
              commit(date);
              setText(formatDisplay(date));
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
      {showHint && <p className="px-1 text-xs text-muted-foreground">{formatDisplay(liveParsed)}</p>}
    </div>
  );
}
